import { sendUpdateUserPreferredCommunicationMethods } from "@/models/socket/user.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Checkbox } from "@/shadcn/components/ui/checkbox";
import { Label } from "@/shadcn/components/ui/label";
import { UserProfileDetails } from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import React, { useContext, useState } from "react";

type PreferCommProps = {
  initialValue: UserProfileDetails["preferredCommunicationMethods"];
};

const PreferComm: React.FC<PreferCommProps> = ({ initialValue }) => {
  const [submitting, setSubmitting] = useState(false);
  const [preferredCommunicationMethods, setPreferredCommunicationMethods] =
    useState<UserProfileDetails["preferredCommunicationMethods"]>(initialValue);
  const { setUserShouldUpdateAccount } = useContext(AppContext);

  async function handleCommunicationMethodChange(
    method: "email" | "sms",
    checked: boolean
  ) {
    // ensure at least one method is selected
    if (!checked && preferredCommunicationMethods.length === 1) {
      genericFailureToast(
        "You must select at least one preferred communication method."
      );
      return;
    }

    setSubmitting(true);
    const updatedMethods = checked
      ? [...preferredCommunicationMethods, method]
      : preferredCommunicationMethods.filter((m) => m !== method);

    setPreferredCommunicationMethods(updatedMethods);

    // Simulate API call to update preferred communication methods
    const response =
      await sendUpdateUserPreferredCommunicationMethods(updatedMethods);

    if (response.rStatus === "error") {
      setSubmitting(false);
      setPreferredCommunicationMethods(initialValue);
      genericFailureToast(
        response.rData ||
          "Failed to update preferred communication methods. Please try again later."
      );
      return;
    }

    setSubmitting(false);
    genericSuccessToast("Preferred communication methods updated successfully");
    setUserShouldUpdateAccount(false);
  }

  return (
    <div>
      <div className="flex flex-col gap-2">
        <div className="font-semibold select-none">
          Preferred Communication Methods
        </div>
        <Label className="flex w-min items-center gap-2">
          <Checkbox
            id="preferredCommunicationMethodEmail"
            checked={preferredCommunicationMethods.includes("email")}
            onCheckedChange={(e) => {
              handleCommunicationMethodChange("email", Boolean(e));
            }}
            disabled={submitting}
          />
          <span
            className={`select-none ${submitting ? "cursor-not-allowed opacity-50 duration-500" : ""}`}
          >
            Email
          </span>
        </Label>
        <Label className="flex w-min items-center gap-2">
          <Checkbox
            id="preferredCommunicationMethodSMS"
            checked={preferredCommunicationMethods.includes("sms")}
            onCheckedChange={(e) => {
              handleCommunicationMethodChange("sms", Boolean(e));
            }}
            disabled={submitting}
          />
          <span
            className={`select-none ${submitting ? "cursor-not-allowed opacity-50 duration-500" : ""}`}
          >
            SMS
          </span>
        </Label>
      </div>
    </div>
  );
};

export default PreferComm;
